import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import { toast } from 'react-toastify'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import APIService from 'services/APIService'
import { RAG, scoreColour, severityColour } from 'features/dashboard/charts/scoreColour'
import { originMeta } from 'features/taskManagement/taskOrigin'
import KpiReportDialog, { downloadKpiPdf, kpiFiltersFromQuery, kpiReportUrl, kpiRequestParams } from 'features/dashboard/sections/KpiReportDialog'
import InfoPopover, { PopLead } from 'features/actions/workspace/InfoPopover'
import { NOT_ENOUGH_DATA, formatRate, performanceCards, rateColour, severityRows, sourceRows } from 'features/actions/workspace/remedialPerformance'

const api = APIService()

const KpiReportPage = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryKey = searchParams.toString()
  const legacyPeriod = !searchParams.get('from') && !searchParams.get('to') && ['month', 'quarter', 'year'].includes(searchParams.get('period'))
    ? searchParams.get('period')
    : null
  const filters = useMemo(() => kpiFiltersFromQuery(searchParams), [queryKey])
  const [report, setReport] = useState(null)
  const [error, setError] = useState(null)
  const [downloading, setDownloading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)

  useEffect(() => {
    setReport(null)
    setError(null)
    const base = kpiRequestParams(filters)
    const params = legacyPeriod
      ? { ...base, from: undefined, to: undefined, period: legacyPeriod, ...(searchParams.get('month') ? { month: searchParams.get('month') } : {}) }
      : base
    api.get('/me/dashboard/kpi-report', { params })
      .then(res => setReport(res?.data?.data || null))
      .catch(() => setError('Could not load the KPI report.'))
  }, [queryKey])

  const currentFilters = useMemo(() => {
    if (!report) return filters
    const withNames = {
      ...filters,
      scope: filters.scope.type === 'all' ? filters.scope : { ...filters.scope, label: report.scopeLabel || filters.scope.label },
      assignee: filters.assignee ? { ...filters.assignee, name: report.assignee?.name || filters.assignee.name } : null,
    }
    return legacyPeriod && report.range
      ? { ...withNames, period: { preset: 'custom', from: report.range.start, to: report.range.end } }
      : withNames
  }, [filters, report, legacyPeriod])

  const applyFilters = next => {
    const url = kpiReportUrl(next)
    setSearchParams(new URLSearchParams(url.split('?')[1] || ''), { replace: true })
  }

  const handleDownload = async () => {
    if (downloading) return
    setDownloading(true)
    try {
      await downloadKpiPdf(currentFilters)
    } catch (err) {
      toast.error('Could not generate the report PDF. Please try again.')
    } finally {
      setDownloading(false)
    }
  }

  if (error) return <Page><Empty>{error}</Empty></Page>
  if (!report) {
    return (
      <Page>
        <Sheet aria-busy='true' aria-label='Loading the KPI report'>
          <SkelLine $w='30%' />
          <SkelLine $w='55%' $h={28} />
          <SkelGrid>{Array.from({ length: 4 }).map((_, i) => <SkelBox key={i} />)}</SkelGrid>
          <SkelLine $w='40%' />
          {Array.from({ length: 4 }).map((_, i) => <SkelLine key={i} $w='100%' />)}
        </Sheet>
      </Page>
    )
  }

  const inPeriod = report.range ? `in ${report.label}` : 'at any time'
  const t = report.tasks || {}
  const a = report.actions || {}

  return (
    <Page>
      <Toolbar>
        <BackBtn onClick={() => navigate('/dashboard')}>
          <ArrowBackRoundedIcon style={{ fontSize: 15 }} />
          Dashboard
        </BackBtn>
        <ToolbarRight>
          <ChangeBtn type='button' onClick={() => setDialogOpen(true)} data-tour-id='kpiReport.change-filters'>
            <TuneRoundedIcon style={{ fontSize: 16 }} />
            Change what is included
          </ChangeBtn>
          <DownloadBtn onClick={handleDownload} disabled={downloading} data-tour-id='kpiReport.download-btn'>
            <FileDownloadRoundedIcon style={{ fontSize: 15 }} />
            {downloading ? 'Preparing…' : 'Download PDF'}
          </DownloadBtn>
        </ToolbarRight>
      </Toolbar>

      <FilterSummary aria-label='What this report covers' data-tour-id='kpiReport.summary'>
        <FilterItem><span>Sites</span>{report.filters?.sites || report.scopeLabel || 'All Building Passports'}</FilterItem>
        <FilterItem><span>Dates</span>{report.range ? report.label : 'All time'}</FilterItem>
        <FilterItem><span>Task type</span>{report.filters?.taskTypes || report.originLabel || 'All task types'}</FilterItem>
        {report.filters?.person && <FilterItem><span>Person</span>{report.filters.person}</FilterItem>}
      </FilterSummary>

      <KpiReportDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        initial={currentFilters}
        primaryAction='apply'
        onApply={applyFilters}
      />

      <Sheet>
        <SheetHead>
          <div>
            <Eyebrow>KPI report · {report.scopeLabel || 'All Building Passports'}</Eyebrow>
            <Title>{report.label}</Title>
          </div>
          <BigRate $tint={scoreColour(t.onTimeRate)}>
            {t.onTimeRate === null || t.onTimeRate === undefined ? '—' : `${Math.round(t.onTimeRate)}%`}
            <BigRateLabel>on-time completion</BigRateLabel>
          </BigRate>
        </SheetHead>

        <SectionTitle>Tasks due {inPeriod}</SectionTitle>
        <KpiGrid>
          <KpiCell><KpiValue>{t.due ?? 0}</KpiValue><KpiLabel>Due</KpiLabel></KpiCell>
          <KpiCell $tint='#2aa6b3'><KpiValue>{t.onTime ?? 0}</KpiValue><KpiLabel>Completed on time</KpiLabel></KpiCell>
          <KpiCell $tint='#d97706'><KpiValue>{t.late ?? 0}</KpiValue><KpiLabel>Completed late</KpiLabel></KpiCell>
          <KpiCell $tint='#b00020'><KpiValue>{t.missed ?? 0}</KpiValue><KpiLabel>Not completed</KpiLabel></KpiCell>
        </KpiGrid>

        {(report.byOrigin || []).some(r => r.due > 0) && (
          <>
            <SectionTitle>By task type</SectionTitle>
            <SevTable>
              <thead>
                <tr>
                  <Th>Task type</Th><ThRight>Due</ThRight><ThRight>On time</ThRight>
                  <ThRight>Late</ThRight><ThRight>Not completed</ThRight><ThRight>Rate</ThRight>
                </tr>
              </thead>
              <tbody>
                {report.byOrigin.filter(r => r.due > 0).map(row => (
                  <tr key={row.origin}>
                    <Td title={originMeta(row.origin)?.description} style={{ textTransform: 'none' }}>
                      <SevDot $tint={originMeta(row.origin)?.color || '#64748b'} />
                      {row.label}
                    </Td>
                    <TdRight>{row.due}</TdRight>
                    <TdRight>{row.onTime}</TdRight>
                    <TdRight>{row.late}</TdRight>
                    <TdRight $alert={row.missed > 0}>{row.missed}</TdRight>
                    <TdRight>
                      <RateChip $tint={scoreColour(row.onTimeRate)}>
                        {row.onTimeRate === null ? '-' : `${Math.round(row.onTimeRate)}%`}
                      </RateChip>
                    </TdRight>
                  </tr>
                ))}
              </tbody>
            </SevTable>
            <MovementLine>
              Corrective tasks were created from remedials to fix a problem. Obligation tasks keep a compliance obligation met. User-Defined tasks are other planned work you have scheduled yourself.
            </MovementLine>
          </>
        )}

        <SectionTitle>Outstanding remedials by risk rating</SectionTitle>
        <SevTable>
          <thead>
            <tr><Th>Risk rating</Th><ThRight>Open</ThRight><ThRight>Of which overdue</ThRight></tr>
          </thead>
          <tbody>
            {(a.openBySeverity || []).map(row => (
              <tr key={row.severity}>
                <Td>
                  <SevDot $tint={severityColour(row.severity)} />
                  {row.severity}
                </Td>
                <TdRight>{row.open}</TdRight>
                <TdRight $alert={row.overdue > 0}>{row.overdue}</TdRight>
              </tr>
            ))}
          </tbody>
        </SevTable>
        <MovementLine>
          {a.opened ?? 0} raised and {a.resolved ?? 0} resolved {report.range ? `during ${report.label}` : 'in total'}. Raised includes remedials still awaiting review and ones later dismissed.
        </MovementLine>

        {report.remedialPerformance && (
          <>
            <SectionTitle>Remedial performance {inPeriod}</SectionTitle>
            <KpiGrid>
              {performanceCards(report.remedialPerformance.totals).map(card => (
                <KpiCell key={card.key} $tint={card.level ? RAG[card.level].fg : undefined}>
                  <KpiValue $muted={card.value === NOT_ENOUGH_DATA}>{card.value}</KpiValue>
                  <KpiLabel>
                    {card.label}
                    <InfoPopover label={`What does ${card.label.toLowerCase()} mean?`} title={card.label}>
                      <PopLead>{card.explain}</PopLead>
                    </InfoPopover>
                  </KpiLabel>
                </KpiCell>
              ))}
            </KpiGrid>
            <TableScroll>
              <RemedialTable>
                <thead>
                  <tr><Th>Severity</Th><ThRight>Raised</ThRight><ThRight>Resolved</ThRight><ThRight>On time</ThRight><ThRight>Late</ThRight><ThRight>Overdue now</ThRight><ThRight>Rate</ThRight></tr>
                </thead>
                <tbody>
                  {severityRows(report.remedialPerformance.bySeverity).map(row => (
                    <tr key={row.severity}>
                      <Td><SevDot $tint={RAG[row.severity].fg} />{row.name}</Td>
                      <TdRight>{row.opened}</TdRight>
                      <TdRight>{row.resolved}</TdRight>
                      <TdRight>{row.resolvedOnTime}</TdRight>
                      <TdRight>{row.resolvedLate}</TdRight>
                      <TdRight $alert={row.overdue > 0}>{row.overdue}</TdRight>
                      <TdRight><RateChip $tint={rateColour(row.onTimeRate) || '#6b7280'}>{formatRate(row.onTimeRate)}</RateChip></TdRight>
                    </tr>
                  ))}
                </tbody>
              </RemedialTable>
            </TableScroll>
            <TableScroll>
              <RemedialTable>
                <thead>
                  <tr><Th>Where they came from</Th><ThRight>Raised</ThRight><ThRight>Resolved</ThRight><ThRight>Overdue now</ThRight><ThRight>Rate</ThRight></tr>
                </thead>
                <tbody>
                  {sourceRows(report.remedialPerformance.bySource).map(row => (
                    <tr key={row.source}>
                      <Td style={{ textTransform: 'none' }}>{row.name}</Td>
                      <TdRight>{row.opened}</TdRight>
                      <TdRight>{row.resolved}</TdRight>
                      <TdRight $alert={row.overdue > 0}>{row.overdue}</TdRight>
                      <TdRight><RateChip $tint={rateColour(row.onTimeRate) || '#6b7280'}>{formatRate(row.onTimeRate)}</RateChip></TdRight>
                    </tr>
                  ))}
                </tbody>
              </RemedialTable>
            </TableScroll>
            <MovementLine>
              Raised counts every remedial created in this period, from any source, including ones still awaiting review and ones later dismissed. On time means resolved on or before the due date it had that day; remedials resolved with no due date are left out of the rate. Overdue now is as things stand today. Task type and person filters apply to tasks only; this section covers every remedial at the chosen sites that have task management switched on, leaving out dormant buildings unless you chose one on its own.
            </MovementLine>
          </>
        )}

        {(report.incidents || []).length > 0 && (
          <>
            <SectionTitle>Incidents {report.range ? `in ${report.label}` : 'recorded'}</SectionTitle>
            <SevTable>
              <thead>
                <tr>
                  <Th>Incident</Th><Th>Type</Th><Th>Severity</Th><ThRight>Date</ThRight>
                </tr>
              </thead>
              <tbody>
                {report.incidents.map((row, i) => (
                  <tr key={row.id || i}>
                    <Td>{row.narrative ? (row.narrative.length > 90 ? `${row.narrative.slice(0, 90)}…` : row.narrative) : '—'}</Td>
                    <Td style={{ textTransform: 'capitalize' }}>
                      {(row.incidentType || 'incident').replace(/_/g, ' ')}
                    </Td>
                    <Td>
                      <SevDot $tint={severityColour(row.severity)} />
                      {row.severity}
                    </Td>
                    <TdRight>{row.occurredAt ? new Date(row.occurredAt).toLocaleDateString('en-GB') : '—'}</TdRight>
                  </tr>
                ))}
              </tbody>
            </SevTable>
          </>
        )}

        <SectionTitle>Performance by building</SectionTitle>
        {(report.perBp || []).length === 0 ? (
          <MovementLine>No tasks were due {inPeriod}.</MovementLine>
        ) : (
          <SevTable>
            <thead>
              <tr>
                <Th>Building</Th><ThRight>Due</ThRight><ThRight>On time</ThRight>
                <ThRight>Late</ThRight><ThRight>Missed</ThRight><ThRight>Rate</ThRight>
              </tr>
            </thead>
            <tbody>
              {report.perBp.map(row => (
                <tr key={row.bpId}>
                  <Td>{row.name}</Td>
                  <TdRight>{row.due}</TdRight>
                  <TdRight>{row.onTime}</TdRight>
                  <TdRight>{row.late}</TdRight>
                  <TdRight $alert={row.missed > 0}>{row.missed}</TdRight>
                  <TdRight>
                    <RateChip $tint={scoreColour(row.onTimeRate)}>
                      {row.onTimeRate === null ? '—' : `${Math.round(row.onTimeRate)}%`}
                    </RateChip>
                  </TdRight>
                </tr>
              ))}
            </tbody>
          </SevTable>
        )}

        <Footnote>
          Generated from live platform data. On-time = completed on or before the deadline; rates computed against
          tasks whose deadline fell within the reporting period.
        </Footnote>
      </Sheet>
    </Page>
  )
}

export default KpiReportPage

const Page = styled.div`
  max-width: 840px;
  margin: 0 auto;
  padding: 24px 24px 64px;
  @media (max-width: 600px) { padding: 16px 12px 48px; }
`
const Toolbar = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
`
const ToolbarRight = styled.div` display: inline-flex; gap: 8px; align-items: center; flex-wrap: wrap; `
const ChangeBtn = styled.button`
  font: inherit; display: inline-flex; align-items: center; gap: 6px;
  min-height: 40px; padding: 8px 14px; border-radius: 999px; cursor: pointer;
  border: 1px solid #d0d5dd; background: #fff; color: #0C1229;
  font-size: 13px; font-weight: 700;
  &:hover { border-color: #33becc; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`
const FilterSummary = styled.div`
  display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 14px;
`
const FilterItem = styled.div`
  display: inline-flex; align-items: baseline; gap: 6px; max-width: 100%;
  padding: 6px 12px; border-radius: 999px; background: #f4f5f9; border: 1px solid #e6e8f0;
  font-size: 13px; font-weight: 700; color: #101828;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  span { font-size: 11px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; color: #667085; }
`
const skelPulse = keyframes`0%,100%{opacity:.55}50%{opacity:.9}`
const SkelLine = styled.div`
  width: ${({ $w }) => $w}; height: ${({ $h }) => $h || 14}px; margin-bottom: 12px;
  border-radius: 6px; background: #eef0f5; animation: ${skelPulse} 1.3s ease-in-out infinite;
`
const SkelGrid = styled.div` display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; margin: 8px 0 20px; `
const SkelBox = styled.div` height: 70px; border-radius: 10px; background: #eef0f5; animation: ${skelPulse} 1.3s ease-in-out infinite; `
const BackBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 14px;
  background: transparent;
  border: 1px solid #ececec;
  color: #0C1229;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: #f4f4f6; }
`
const DownloadBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px;
  background: #0C1229;
  color: #fff;
  border: none;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: default; }
`
const Sheet = styled.div`
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  padding: 32px 36px;
  @media (max-width: 600px) { padding: 20px 16px; }
  @media print { border: none; border-radius: 0; padding: 0; }
`
const SheetHead = styled.header`
  display: flex; align-items: center; justify-content: space-between; gap: 24px;
  border-bottom: 3px solid #0C1229;
  padding-bottom: 16px;
  margin-bottom: 8px;
  flex-wrap: wrap;
`
const Eyebrow = styled.div`
  font-size: 10.5px; letter-spacing: 0.16em; text-transform: uppercase;
  color: #6b7280; font-weight: 800;
`
const Title = styled.h1`
  margin: 0;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 30px;
  letter-spacing: -0.7px;
  color: #0C1229;
`
const BigRate = styled.div`
  display: flex; flex-direction: column; align-items: flex-end;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 44px;
  line-height: 1;
  color: ${({ $tint }) => $tint};
  font-variant-numeric: tabular-nums;
`
const BigRateLabel = styled.span`
  font-family: system-ui, sans-serif;
  font-size: 10px;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  font-weight: 800;
  color: #6b7280;
  margin-top: 6px;
`
const SectionTitle = styled.h2`
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 800;
  margin: 24px 0 10px;
  color: #0C1229;
`
const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  @media (max-width: 600px) { grid-template-columns: repeat(2, 1fr); }
`
const KpiCell = styled.div`
  border: 1px solid ${({ $tint }) => $tint ? `${$tint}40` : '#ececec'};
  background: ${({ $tint }) => $tint ? `${$tint}0D` : '#fafbfc'};
  border-radius: 10px;
  padding: 14px;
  display: flex; flex-direction: column; gap: 2px;
`
const KpiValue = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 28px;
  line-height: 1;
  color: #0C1229;
  font-variant-numeric: tabular-nums;
  ${p => p.$muted && `font-family: inherit; font-size: 15px; line-height: 1.3; color: #6b7280;`}
`
const KpiLabel = styled.span`
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 800;
  color: #6b7280;
`
const SevTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  tr { border-bottom: 1px solid #f4f4f6; }
`
const RemedialTable = styled(SevTable)`
  min-width: 560px;
  th, td { white-space: nowrap; }
`
const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`
const Th = styled.th`
  text-align: left;
  padding: 6px 8px 6px 0;
  font-size: 10px;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: #6b7280;
`
const ThRight = styled(Th)` text-align: right; padding-right: 0; `
const Td = styled.td`
  padding: 8px 8px 8px 0;
  font-size: 12.5px;
  color: #1e293b;
  text-transform: capitalize;
`
const TdRight = styled.td`
  padding: 8px 0;
  font-size: 12.5px;
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: ${({ $alert }) => $alert ? '#b00020' : '#1e293b'};
  font-weight: ${({ $alert }) => $alert ? 700 : 400};
`
const SevDot = styled.span`
  display: inline-block;
  width: 8px; height: 8px;
  border-radius: 50%;
  background: ${({ $tint }) => $tint || '#9aa0aa'};
  margin-right: 8px;
`
const RateChip = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  background: ${({ $tint }) => `${$tint}14`};
  color: ${({ $tint }) => $tint};
  border: 1px solid ${({ $tint }) => `${$tint}33`};
  font-size: 11.5px;
  font-weight: 800;
`
const MovementLine = styled.p`
  margin: 8px 0 0;
  font-size: 12px;
  color: #6b7280;
`
const Empty = styled.div`
  padding: 48px;
  text-align: center;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  color: #6b7280;
`
const Footnote = styled.p`
  margin-top: 28px;
  font-size: 10.5px;
  color: #6b7280;
  border-top: 1px solid #ececec;
  padding-top: 12px;
  line-height: 1.5;
`
